import type {
  BrandSpecial,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { EventNames, coreProductProperties } from '@iheartjane/tracking';
import type { AppMode, PriceId, ReservationMode } from '@iheartjane/types';

import type { AlgoliaProduct } from '@jane/search/types';
import type { CalloutVariants, ShareNetwork } from '@jane/shared-ecomm/types';
// eslint-disable-next-line no-restricted-imports
import type { MenuProduct, Product } from '@jane/shared/models';
import type { StoreSpecial, UserSegmentIds } from '@jane/shared/types';
import {
  getActualUnitPrice,
  shouldProductShowSpecial,
} from '@jane/shared/util';

import type {
  BrandDetailPageTap,
  BundlePromptTap,
  CashBackDetailPageTap,
  ChangeBankAccountSuccess,
  ClickedBrandLink,
  DispensaryTap,
  HomeTap,
  LinkBankAccountEvent,
  LoadedLoyaltyPoints,
  LoggedIn,
  MenuTabTap,
  NavBarTap,
  ProductListingTap,
  ProductReview,
  ProductReviewInteractionTypes,
  SSOChannel,
  StoreInfoAttribute,
  StoreSpecialPromptTap,
  UserPreferencesType,
} from './event.types';
import { track } from './track';

export const trackCartClick = () => track({ event: EventNames.ClickedCart });

export const trackTimeSpentOnMenu = ({
  storeId,
  seconds,
}: {
  seconds: number;
  storeId: string | number;
}) => track({ event: EventNames.TimedMenuLoad, storeId, seconds });

export const trackStoresPageLoad = ({ map }: { map: boolean }) =>
  track({ event: EventNames.LoadedStoresPage, map });

export const trackHomePageLoad = () =>
  track({ event: EventNames.LoadedHomePage });

export const trackClickedBrandLink = ({
  appMode,
  brandName,
  page,
  storeId,
}: Omit<ClickedBrandLink, 'event'>) =>
  track({
    event: EventNames.ClickedBrandLink,
    appMode,
    brandName,
    page,
    storeId,
  });

export const trackOpenedPayByLink = (source: string) =>
  track({ event: EventNames.OpenedPayByLink, source });

export const trackClickedProceedToPayment = () =>
  track({ event: EventNames.ClickedProceedToPayment });

export const trackViewedPaymentSummary = (currentStep: string) =>
  track({ event: EventNames.ViewedPaymentSummary, currentStep });

export const trackOpenedAccordionItem = (itemName: string) =>
  track({ event: EventNames.OpenedAccordionItem, itemName });

export const trackCheckoutClick = (reservationMode: ReservationMode) =>
  track({ event: EventNames.ClickedCheckout, reservationMode });

interface LoadedCartPageProperties {
  isEmpty: boolean;
  reservationMode: ReservationMode;
}
export const trackLoadedCartPage = (props: LoadedCartPageProperties) =>
  track({ event: EventNames.LoadedCartPage, ...props });

export const trackLoadedCheckoutPage = (reservationMode: ReservationMode) =>
  track({ event: EventNames.LoadedCheckoutPage, reservationMode });

export const trackChangeBankAccountTap = () => {
  track({ event: EventNames.ChangeBankAccountTap });
};

interface InteractedWithProductReviewProperties {
  aggregateRating?: number | null;
  interactionType: ProductReviewInteractionTypes;
  interactionValue: unknown;
  product: Product;
  reviewCount?: number | null;
  userSegments?: UserSegmentIds;
}
export const trackInteractedWithProductReview = ({
  product,
  interactionType,
  interactionValue,
  aggregateRating,
  reviewCount,
  userSegments,
}: InteractedWithProductReviewProperties) =>
  track({
    event: EventNames.InteractedWithProductReviews,
    interactionType,
    interactionValue,
    ...coreProductProperties(product, userSegments),
    aggregateRating,
    reviewCount,
  });

export const trackLoadedCrmPoints = ({
  storeHasProvider,
  memberPoints,
  crmProvider,
  crmRewards,
}: Omit<LoadedLoyaltyPoints, 'event'>) =>
  track({
    event: EventNames.LoadedLoyaltyPoints,
    storeHasProvider,
    memberPoints,
    crmProvider,
    crmRewards,
  });

export const trackReviewedProduct = (
  product: Product,
  review: ProductReview,
  activities: string[],
  feelings: string[],
  userSegments?: UserSegmentIds
) =>
  track({
    event: EventNames.ReviewedProduct,
    activities,
    review,
    feelings,
    ...coreProductProperties(product, userSegments),
  });

export const trackLoadedProductReviewForm = () =>
  track({ event: EventNames.LoadedWriteProductReviewForm });

export const trackContinueShoppingClick = () =>
  track({ event: EventNames.ClickedContinueShopping });

export const trackStoreInfoClick = (attribute: StoreInfoAttribute) =>
  track({ event: EventNames.ClickedStoreInfo, attribute });

export const trackSharedProduct = ({
  productId,
  storeId,
  via,
}: {
  productId: string;
  storeId?: string;
  via: ShareNetwork;
}) => track({ event: EventNames.SharedProduct, productId, storeId, via });

export const trackCustomerLogin = ({
  ssoChannel,
  source,
}: Omit<LoggedIn, 'event'>) => {
  track({ event: EventNames.LoggedIn, ssoChannel, source });
};

interface RegistrationProps {
  customer: { id: number; ssoChannel?: SSOChannel };
  partnerChannel: AppMode;
  partnerId?: string | number;
  partnerName?: string;
}

export const trackCustomerRegistration = ({
  customer,
  partnerChannel,
  partnerId,
  partnerName,
}: RegistrationProps) => {
  janeMixpanel.alias(customer.id.toString());
  track({
    event: EventNames.Registered,
    ssoChannel: customer.ssoChannel,
    partnerChannel,
    partnerId,
    partnerName,
  });
};

interface StoreCommunicationBannerProps {
  bannerEnabled: boolean;
  bannerMessage: string | null | undefined;
  isExpanded: boolean;
  storeId: string | number;
}

export const trackStoreCommunicationBannerLoad = ({
  storeId,
  bannerEnabled,
  bannerMessage,
  isExpanded,
}: StoreCommunicationBannerProps) => {
  const htmlRegex = /<[^>]*>/g;
  const standardizedMessage = bannerMessage?.replace(htmlRegex, '') || '';

  track({
    event: EventNames.StoreCommunicationBannerLoaded,
    storeId,
    bannerEnabled,
    bannerMessageCharacterCount: bannerEnabled ? standardizedMessage.length : 0,
    isExpanded,
  });
};

type BrandDetailPageTapType = Omit<BrandDetailPageTap, 'event'>;

export const trackBrandDetailPageTap = ({
  brandDiscount,
  brandId,
  brandName,
  brandRankInList,
  cityState,
  source,
  storeFulfillmentType,
  storeSearchRadius,
  storeType,
  zipcode,
}: BrandDetailPageTapType) => {
  track({
    brandDiscount,
    brandId,
    brandName,
    brandRankInList,
    cityState,
    event: EventNames.BrandDetailPageTap,
    source,
    storeFulfillmentType,
    storeSearchRadius,
    storeType,
    zipcode,
  });
};

interface BrandPageProps {
  brandId: string | number;
  brandName: string;
  productReferrerId?: string | number;
}

export const trackBrandPageLoad = ({
  brandId,
  brandName,
  productReferrerId,
}: BrandPageProps) => {
  track({
    event: EventNames.BrandPageLoaded,
    brandId,
    brandName,
    productReferrerId,
  });
};

export const trackBrandPageOpenVideo = (brandId: string | number) => {
  track({
    event: EventNames.BrandPageOpenedVideo,
    brandId,
  });
};

type HomeTapType = Omit<HomeTap, 'event'>;

export const trackHomeTap = ({
  cityState,
  section,
  storeFulfillmentType,
  storeSearchRadius,
  storeType,
  tile,
  zipcode,
}: HomeTapType) => {
  track({
    cityState,
    event: EventNames.HomeTap,
    section,
    storeFulfillmentType,
    storeSearchRadius,
    storeType,
    tile,
    zipcode,
  });
};

type NavBarTapType = Omit<NavBarTap, 'event'>;

export const trackNavBarTap = ({
  cityState,
  navBarTap,
  source,
  storeFulfillmentType,
  storeSearchRadius,
  storeType,
  zipcode,
}: NavBarTapType) => {
  track({
    cityState,
    navBarTap,
    event: EventNames.NavBarTap,
    source,
    storeFulfillmentType,
    storeSearchRadius,
    storeType,
    zipcode,
  });
};

interface CashBackDetailPageTapType extends Omit<
  CashBackDetailPageTap,
  | 'event'
  | 'brandDiscountDetails'
  | 'brandDiscountId'
  | 'brandDiscountType'
  | 'brandId'
  | 'discountBrandName'
> {
  brandSpecial: Partial<BrandSpecial> &
    Pick<
      BrandSpecial,
      | 'id'
      | 'discount_type'
      | 'product_brand_id'
      | 'discount_dollar_amount'
      | 'discount_percent'
      | 'discount_target_price'
    >;
}

export const trackCashBackDetailPageTap = ({
  brandSpecial,
  brandDiscountRankInList,
  brandName,
  cityState,
  source,
  storeFulfillmentType,
  storeSearchRadius,
  storeType,
  storeId,
  storeName,
  storeCity,
  storeState,
  zipcode,
}: CashBackDetailPageTapType) => {
  let brandDiscountDetails = brandSpecial.discount_dollar_amount;
  brandDiscountDetails =
    brandSpecial.discount_type === 'percent'
      ? brandSpecial.discount_percent
      : brandDiscountDetails;
  brandDiscountDetails =
    brandSpecial.discount_type === 'target_price'
      ? brandSpecial.discount_target_price
      : brandDiscountDetails;
  track({
    brandDiscountDetails,
    brandDiscountId: brandSpecial.id,
    brandDiscountType: brandSpecial.discount_type,
    brandId: brandSpecial.product_brand_id,
    brandDiscountRankInList: brandDiscountRankInList + 1,
    brandName,
    brandRewardType:
      brandSpecial.special_type !== 'product' ? 'bundle' : 'product',
    cityState,
    discountBrandName: brandName,
    event: EventNames.CashBackDetailPageTap,
    source,
    storeFulfillmentType,
    storeId: storeId,
    storeName: storeName,
    storeCity: storeCity,
    storeState: storeState,
    storeSearchRadius,
    storeType,
    zipcode,
  });
};

interface BundlePromptTapType extends Omit<
  BundlePromptTap,
  | 'event'
  | 'brandDiscountDetails'
  | 'brandDiscountId'
  | 'brandDiscountType'
  | 'brandId'
  | 'brandName'
  | 'discountBrandName'
> {
  brandSpecial: Partial<BrandSpecial> &
    Pick<
      BrandSpecial,
      | 'id'
      | 'discount_type'
      | 'product_brand_id'
      | 'discount_dollar_amount'
      | 'discount_percent'
      | 'discount_target_price'
      | 'product_brand'
    >;
}

export const trackBundlePromptTap = ({
  brandSpecial,
  cityState,
  source,
  storeFulfillmentType,
  storeSearchRadius,
  storeType,
  storeId,
  storeName,
  storeCity,
  storeState,
}: BundlePromptTapType) => {
  const brandDiscountDetails =
    brandSpecial.discount_type === 'percent'
      ? brandSpecial.discount_percent
      : brandSpecial.discount_type === 'target_price'
        ? brandSpecial.discount_target_price
        : brandSpecial.discount_dollar_amount;
  track({
    brandDiscountDetails,
    brandDiscountId: brandSpecial.id,
    brandDiscountType: brandSpecial.discount_type,
    brandId: brandSpecial.product_brand_id,
    brandName: brandSpecial.product_brand.name,
    cityState,
    discountBrandName: brandSpecial.product_brand.name,
    event: EventNames.BundlePromptTap,
    source,
    storeFulfillmentType,
    storeId: storeId,
    storeName: storeName,
    storeCity: storeCity,
    storeState: storeState,
    storeSearchRadius,
    storeType,
  });
};

interface StoreSpecialPromptTapType extends Omit<
  StoreSpecialPromptTap,
  'event'
> {
  special: StoreSpecial | V3StoreSpecial;
}

export const trackStoreSpecialPromptTap = ({
  special,
  cityState,
  source,
  storeFulfillmentType,
  storeSearchRadius,
  storeType,
  storeId,
  storeName,
  storeCity,
  storeState,
}: StoreSpecialPromptTapType) => {
  const discountDetails =
    special.discount_type === 'percent'
      ? special.discount_percent
      : special.discount_type === 'target_price'
        ? special.discount_target_price
        : special.discount_dollar_amount;
  track({
    discountDetails: discountDetails ?? undefined,
    specialId: special.id,
    discountType: special.discount_type,
    cityState,
    specialName: special.title,
    specialType: special.special_type,
    event: EventNames.StoreSpecialPromptTap,
    source,
    storeFulfillmentType,
    storeId: storeId,
    storeName: storeName,
    storeCity: storeCity,
    storeState: storeState,
    storeSearchRadius,
    storeType,
  });
};

interface ProductListingTapType
  extends
    UserPreferencesType,
    Pick<
      ProductListingTap,
      | 'almostGone'
      | 'columnPosition'
      | 'rowPosition'
      | 'source'
      | 'storeId'
      | 'storeCurrency'
      | 'storeName'
      | 'storeCity'
      | 'storeState'
      | 'productLocation'
    > {
  brandSpecial?: Partial<BrandSpecial> &
    Pick<
      BrandSpecial,
      | 'id'
      | 'discount_type'
      | 'product_brand_id'
      | 'discount_dollar_amount'
      | 'discount_percent'
      | 'discount_target_price'
    >;
  product: MenuProduct | Product | AlgoliaProduct;
  userSegments?: UserSegmentIds;
}

export const trackProductListingTap = ({
  almostGone,
  brandSpecial,
  cityState,
  columnPosition,
  product,
  productLocation,
  rowPosition,
  source,
  storeId,
  storeName,
  storeCity,
  storeState,
  storeFulfillmentType,
  storeSearchRadius,
  storeType,
  userSegments,
  zipcode,
}: ProductListingTapType) => {
  let brandDiscountId =
    product.applicable_brand_special_ids &&
    product.applicable_brand_special_ids[0];
  let brandDiscountDetails = undefined;
  let brandDiscountType = undefined;

  const showProductSpecial = shouldProductShowSpecial({
    product,
    userSegments,
  });

  if (showProductSpecial && brandSpecial) {
    brandDiscountId = brandSpecial.id;
    brandDiscountType = brandSpecial.discount_type;
    brandDiscountDetails = brandSpecial.discount_dollar_amount;
    brandDiscountDetails =
      brandSpecial.discount_type === 'percent'
        ? brandSpecial.discount_percent
        : brandDiscountDetails;
    brandDiscountDetails =
      brandSpecial.discount_type === 'target_price'
        ? brandSpecial.discount_target_price
        : brandDiscountDetails;
  }

  const availableWeights = (product as AlgoliaProduct).available_weights;
  const priceUnit: PriceId = (
    availableWeights && availableWeights.length > 0
      ? availableWeights[0]
      : 'each'
  ) as PriceId;

  const pricePerUnit = getActualUnitPrice(product as MenuProduct, priceUnit);

  const coreProperties = coreProductProperties(product, userSegments);

  track({
    event: EventNames.ProductListingTap,
    ...coreProperties,
    productId: coreProperties.productId ?? null,
    almostGone,
    brandDiscountId: brandDiscountId || coreProperties.brandDiscountId,
    brandDiscountDetails,
    brandDiscountType,
    brandRewardType: brandSpecial
      ? brandSpecial?.special_type === 'product'
        ? 'product'
        : 'bundle'
      : undefined,
    cityState,
    columnPosition,
    productLocation,
    pricePerUnit,
    rowPosition,
    source,
    storeId,
    storeName,
    storeCity,
    storeState,
    storeFulfillmentType,
    storeSearchRadius,
    storeType,
    zipcode,
  });
};

export const linkBankAccountEvent = ({
  email,
  event,
  isJanePay,
  source,
  username,
}: Omit<LinkBankAccountEvent, 'source'> & { source?: CalloutVariants }) => {
  const getSource = (): LinkBankAccountEvent['source'] => {
    if (
      !source ||
      [
        EventNames.LinkBankAccountFailed,
        EventNames.UnlinkedLastBankAccount,
      ].includes(event)
    ) {
      return source;
    }

    return `${source}${isJanePay ? ' (Pay)' : ' (Gold)'}`;
  };

  track({
    email,
    event,
    source: getSource(),
    username,
  });
};

export const trackMenuTabTap = ({
  source,
  appMode,
  storeId,
  storeName,
  storeCity,
  storeState,
  storeCurrency,
  productLocation,
}: Omit<MenuTabTap, 'event'>) => {
  track({
    event: EventNames.MenuTabTap,
    source,
    appMode,
    storeId,
    storeName,
    storeCity,
    storeState,
    storeCurrency,
    productLocation,
  });
};

export const trackChangeBankAccountSuccess = ({
  source,
}: Omit<ChangeBankAccountSuccess, 'event'>) => {
  track({
    event: EventNames.ChangeBankAccountSuccess,
    source,
  });
};

export const trackDispensaryTap = (props: Omit<DispensaryTap, 'event'>) => {
  track({
    event: EventNames.DispensaryTap,
    ...props,
  });
};
